type SwipeEvent = SwipeUpEvent | SwipeDownEvent | SwipeLeftEvent | SwipeRightEvent;

export class SwipeUpEvent extends CustomEvent<Motion> {
	static type = 'swipeup';
	constructor(motion: Motion) { super(SwipeUpEvent.type, { detail: motion }); }
}

export class SwipeDownEvent extends CustomEvent<Motion> {
	static type = 'swipedown';
	constructor(motion: Motion) { super(SwipeDownEvent.type, { detail: motion }); }
}

export class SwipeLeftEvent extends CustomEvent<Motion> {
	static type = 'swipeleft';
	constructor(motion: Motion) { super(SwipeLeftEvent.type, { detail: motion }); }
}

export class SwipeRightEvent extends CustomEvent<Motion> {
	static type = 'swiperight';
	constructor(motion: Motion) { super(SwipeRightEvent.type, { detail: motion }); }
}

export interface Motion {
	horizontal: boolean;
	distance: number;
	delta: number;
	dx: number;
	dy: number;
	duration: number;
}

export interface TouchPart {
	touch: Touch;
	timeStamp: number;
}

export interface SwipeConfig {
	swipe_delta: number;
}

const default_config: SwipeConfig = {
	swipe_delta: 10,
};

export default class SwipeHandler {
	targets: NodeList;
	history: TouchPart[];
	pending: boolean;
	config: SwipeConfig;

	constructor() {
		this.history = [];
		this.pending = false;
	}

	init(targets: NodeList, config: SwipeConfig = default_config): void {
		this.targets = targets;
		targets.forEach(target => {
			target.addEventListener('touchstart', (e: TouchEvent) => this.start(e));
			target.addEventListener('touchend', (e: TouchEvent) => this.end(e));
			target.addEventListener('touchmove', (e: TouchEvent) => this.swiped(e));
			target.addEventListener('touchcancel', (e: TouchEvent) => this.cancel(e));
		});
		this.config = config;
	}

	start(event: TouchEvent): void {
		this.history.push(SwipeHandler.copyTouch(event));
		this.pending = true;
	}

	end(event: TouchEvent): void {
		if (this.pending) {
			this.emit(this.history, event.target);
		}
		this.cancel(event);
	}

	swiped(event: TouchEvent): void {
		if (!this.pending) { return; }
		const new_event = SwipeHandler.copyTouch(event);
		const last_event = this.history.at(-1);
		this.history.push(new_event);
		this.emit([last_event, new_event], event.target);
	}

	cancel(event: TouchEvent): void {
		this.history = [];
		this.pending = false;
	}

	emit(source: Motion | TouchPart[], target: EventTarget): void {
		if (Array.isArray(source)) {
			const motion = this.diff(source.at(0), source.at(-1));
			if (!motion) { return; }
			this._emit(motion, target);
		} else {
			this._emit(source, target);
		}
	}

	protected _emit(motion: Motion, target: EventTarget): void {
		if (motion.distance >= this.config.swipe_delta) {
			if (motion.horizontal) {
				target.dispatchEvent(SwipeHandler.toEvent(motion));
				this.pending = false;
			}
		}
	}

	diff(a?: TouchPart, b?: TouchPart): Motion | undefined {
		if (!a || !b) { return undefined; }
		const dx = b.touch.pageX - a.touch.pageX;
		const dy = b.touch.pageY - a.touch.pageY;
		const horizontal = Math.abs(dx) >= Math.abs(dy);
		const distance = Math.hypot(dx, dy);
		const direction = horizontal ? dx / Math.abs(dx) : dy / Math.abs(dy);
		const duration = b.timeStamp - a.timeStamp;
		return {
			horizontal, distance, dx, dy,
			delta: distance * direction,
			duration,
		}
	}

	// Relocated to static class functions for testability. ---

	static copyTouch(event: TouchEvent): TouchPart {
		const { touches, timeStamp } = event;
		return { touch: touches.item(0), timeStamp };
	}

	static toEvent(motion: Motion): SwipeEvent {
		const Swipe = motion.horizontal ? motion.delta >= 0
				? SwipeRightEvent
				: SwipeLeftEvent
			: motion.delta >= 0
				? SwipeDownEvent
				: SwipeUpEvent;
		return new Swipe(motion);
	}
}